import React, { useEffect, useMemo, useState } from "react";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSelect,
    StandardModalState,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection,
    TextInputWithIcon,
    UserSelectionAvatar,
    UserSelectionCellText,
    UserSelectionCheckButton,
    UserSelectionChip,
    UserSelectionChipBody,
    UserSelectionChipIcon,
    UserSelectionChipText,
    UserSelectionControls,
    UserSelectionCount,
    UserSelectionFieldGroup,
    UserSelectionLabel,
    UserSelectionNameCell,
    UserSelectionRemoveButton,
    UserSelectionSearchInput,
    UserSelectionSelectButton,
    UserSelectionSummaryBar,
    UserSelectionSummaryLabel,
    UserSelectionSwitch,
    UserSelectionTable,
    UserSelectionToggleBox,
} from "src/components";
import SearchIcon from "src/img/new/search-gray.svg";
import UsersIcon from "src/img/new/users.svg";
import CloseIcon from "src/img/CloseButton@2x.png";
import BlueCheck from "src/img/survey/blue-check.svg";
import type { CompanyUser } from "../types";
import { getCompanyUserInitials, getCompanyUserName } from "../calendarData";

type ParticipantGroup = "employees" | "clients";

type CalendarParticipantSelectionModalProps = {
    companyUsers: CompanyUser[];
    companyUsersLoading: boolean;
    currentUserUuid: string;
    onClose: () => void;
    onSave: (participantUuids: string[]) => void;
    open: boolean;
    selectedParticipantUuids: string[];
};

export function CalendarParticipantSelectionModal({
    companyUsers,
    companyUsersLoading,
    currentUserUuid,
    onClose,
    onSave,
    open,
    selectedParticipantUuids,
}: CalendarParticipantSelectionModalProps) {
    const [participantGroup, setParticipantGroup] = useState<ParticipantGroup>("employees");
    const [searchQuery, setSearchQuery] = useState("");
    const [draftParticipantUuids, setDraftParticipantUuids] = useState<string[]>(selectedParticipantUuids);

    useEffect(() => {
        if (open) {
            setDraftParticipantUuids(selectedParticipantUuids.filter(uuid => uuid !== currentUserUuid));
            setSearchQuery("");
        }
    }, [currentUserUuid, open, selectedParticipantUuids]);

    const selectedParticipantSet = useMemo(
        () => new Set(draftParticipantUuids),
        [draftParticipantUuids]
    );
    const selectableCompanyUsers = useMemo(
        () => companyUsers.filter(user => user.uuid !== currentUserUuid),
        [companyUsers, currentUserUuid]
    );
    const employees = useMemo(() => selectableCompanyUsers.filter(user => !user.isClient), [selectableCompanyUsers]);
    const clients = useMemo(() => selectableCompanyUsers.filter(user => user.isClient), [selectableCompanyUsers]);
    const groupUsers = participantGroup === "employees" ? employees : clients;
    const selectedEmployeesCount = employees.filter(user => selectedParticipantSet.has(user.uuid)).length;
    const selectedClientsCount = clients.filter(user => selectedParticipantSet.has(user.uuid)).length;
    const allGroupUsersSelected = groupUsers.length > 0
        && groupUsers.every(user => selectedParticipantSet.has(user.uuid));
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const filteredUsers = groupUsers.filter(user => {
        const searchableValue = [
            getCompanyUserName(user),
            user.email,
            user.department,
            user.employmentPosition,
        ].join(" ").toLowerCase();
        return searchableValue.includes(normalizedSearch);
    });

    const toggleParticipant = (participantUuid: string): void => {
        setDraftParticipantUuids(previous => previous.includes(participantUuid)
            ? previous.filter(uuid => uuid !== participantUuid)
            : [...previous, participantUuid]);
    };

    const toggleAllGroupUsers = (): void => {
        const groupUserUuids = new Set(groupUsers.map(user => user.uuid));
        setDraftParticipantUuids(previous => {
            if (allGroupUsersSelected) {
                return previous.filter(uuid => !groupUserUuids.has(uuid));
            }
            return Array.from(new Set([...previous, ...groupUserUuids]));
        });
    };

    const clearGroup = (group: ParticipantGroup): void => {
        const groupUserUuids = new Set(
            (group === "employees" ? employees : clients).map(user => user.uuid)
        );
        setDraftParticipantUuids(previous => previous.filter(uuid => !groupUserUuids.has(uuid)));
    };

    const groupLabel = participantGroup === "employees" ? "Employees" : "Clients";

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            widthMode="table"
            title="Select Meeting Participants"
            description="Select the Me Business users to invite to this meeting."
            headerAside={(
                <div style={{ width: "13rem" }}>
                    <StandardModalSelect
                        value={participantGroup}
                        onChange={({ target }) => setParticipantGroup(target.value as ParticipantGroup)}
                        disabled={companyUsersLoading}
                    >
                        <option value="employees">Employees</option>
                        <option value="clients">Clients</option>
                    </StandardModalSelect>
                </div>
            )}
            footer={(
                <StandardModalActionGroup style={{ marginLeft: "auto" }}>
                    <StandardModalActionButton type="button" onClick={onClose}>Cancel</StandardModalActionButton>
                    <StandardModalActionButton
                        type="button"
                        $variant="primary"
                        disabled={companyUsersLoading}
                        onClick={() => {
                            onSave(draftParticipantUuids.filter(uuid => uuid !== currentUserUuid));
                            onClose();
                        }}
                    >
                        Save
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionSummaryBar>
                    <UserSelectionSummaryLabel>Selected People:</UserSelectionSummaryLabel>
                    {selectedEmployeesCount > 0 && (
                        <UserSelectionChip>
                            <UserSelectionChipBody>
                                <UserSelectionChipIcon><img src={UsersIcon} alt="" /></UserSelectionChipIcon>
                                <UserSelectionChipText>
                                    {selectedEmployeesCount} {selectedEmployeesCount === 1 ? "Employee" : "Employees"}
                                </UserSelectionChipText>
                            </UserSelectionChipBody>
                            <UserSelectionRemoveButton
                                type="button"
                                onClick={() => clearGroup("employees")}
                                aria-label="Remove selected employees"
                            >
                                <img src={CloseIcon} alt="" />
                            </UserSelectionRemoveButton>
                        </UserSelectionChip>
                    )}
                    {selectedClientsCount > 0 && (
                        <UserSelectionChip>
                            <UserSelectionChipBody>
                                <UserSelectionChipIcon><img src={UsersIcon} alt="" /></UserSelectionChipIcon>
                                <UserSelectionChipText>
                                    {selectedClientsCount} {selectedClientsCount === 1 ? "Client" : "Clients"}
                                </UserSelectionChipText>
                            </UserSelectionChipBody>
                            <UserSelectionRemoveButton
                                type="button"
                                onClick={() => clearGroup("clients")}
                                aria-label="Remove selected clients"
                            >
                                <img src={CloseIcon} alt="" />
                            </UserSelectionRemoveButton>
                        </UserSelectionChip>
                    )}
                </UserSelectionSummaryBar>
            </StandardModalSection>

            <StandardModalSection>
                <UserSelectionControls>
                    <UserSelectionFieldGroup>
                        <UserSelectionLabel>Select {groupLabel}</UserSelectionLabel>
                        <TextInputWithIcon
                            className=""
                            display="block"
                            iconSrc={SearchIcon}
                            iconPosition="right"
                            iconOffset="0.875rem"
                            iconWidth="24px"
                            iconHeight="24px"
                            margin="0"
                            maxWidth="none"
                            style={{}}
                        >
                            <UserSelectionSearchInput
                                type="text"
                                value={searchQuery}
                                placeholder={`Search ${groupLabel.toLowerCase()}`}
                                onChange={({ target }) => setSearchQuery(target.value)}
                                disabled={companyUsersLoading}
                            />
                        </TextInputWithIcon>
                    </UserSelectionFieldGroup>
                    <UserSelectionFieldGroup>
                        <UserSelectionCount>
                            {groupUsers.filter(user => selectedParticipantSet.has(user.uuid)).length} {groupLabel} Selected
                        </UserSelectionCount>
                        <UserSelectionToggleBox>
                            All {groupLabel}
                            <UserSelectionSwitch
                                checked={allGroupUsersSelected}
                                onChange={toggleAllGroupUsers}
                                disabled={companyUsersLoading || groupUsers.length === 0}
                            />
                        </UserSelectionToggleBox>
                    </UserSelectionFieldGroup>
                </UserSelectionControls>

                {companyUsersLoading ? (
                    <StandardModalState style={{ minHeight: "20rem" }}>Loading users...</StandardModalState>
                ) : (
                    <StandardModalTableSection>
                        <StandardModalTableScroll style={{ maxHeight: "min(30rem, calc(100vh - 27rem))" }}>
                            <UserSelectionTable>
                                <StandardModalTable style={{ minWidth: "860px" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: "25%" }}>Name</th>
                                            <th style={{ width: "25%" }}>Email</th>
                                            <th style={{ width: "20%" }}>Department / Group</th>
                                            <th style={{ width: "20%" }}>Job Title</th>
                                            <th style={{ width: "10%" }}>Select User</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.length === 0 && (
                                            <tr>
                                                <td colSpan={5}>
                                                    <StandardModalState style={{ minHeight: "6rem", border: 0 }}>
                                                        No users to display
                                                    </StandardModalState>
                                                </td>
                                            </tr>
                                        )}
                                        {filteredUsers.map(user => {
                                            const selected = selectedParticipantSet.has(user.uuid);
                                            return (
                                                <tr key={user.uuid} className={selected ? "is-selected" : ""}>
                                                    <td>
                                                        <UserSelectionNameCell>
                                                            <UserSelectionAvatar>
                                                                {user.profilePictureUrl
                                                                    ? <img src={user.profilePictureUrl} alt="" />
                                                                    : getCompanyUserInitials(user)}
                                                            </UserSelectionAvatar>
                                                            <UserSelectionCellText>{getCompanyUserName(user)}</UserSelectionCellText>
                                                        </UserSelectionNameCell>
                                                    </td>
                                                    <td>{user.email || "-"}</td>
                                                    <td>{user.department || "-"}</td>
                                                    <td>{user.employmentPosition || "-"}</td>
                                                    <td>
                                                        {selected ? (
                                                            <UserSelectionCheckButton
                                                                type="button"
                                                                onClick={() => toggleParticipant(user.uuid)}
                                                                aria-label={`Deselect ${getCompanyUserName(user)}`}
                                                            >
                                                                <img src={BlueCheck} alt="" />
                                                            </UserSelectionCheckButton>
                                                        ) : (
                                                            <UserSelectionSelectButton
                                                                type="button"
                                                                onClick={() => toggleParticipant(user.uuid)}
                                                                aria-label={`Select ${getCompanyUserName(user)}`}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </StandardModalTable>
                            </UserSelectionTable>
                        </StandardModalTableScroll>
                    </StandardModalTableSection>
                )}
            </StandardModalSection>
        </StandardModal>
    );
}
